import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { Loader2, Plus, Search } from "lucide-react";

type CourseRow = Database["public"]["Tables"]["courses"]["Row"];
type CourseStatus = Database["public"]["Enums"]["course_status"];

export const Route = createFileRoute("/_admin/admin/courses/")({
  component: AdminCoursesIndex,
});

function slugify(s: string) {
  return s
    .toLowerCase()
    .replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss")
    .replace(/[^a-z0-9-]+/g, "-").replace(/(^-|-$)/g, "");
}

const dateFmt = new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });

function AdminCoursesIndex() {
  const navigate = useNavigate();
  const [items, setItems] = useState<CourseRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<CourseStatus | "all">("all");
  const [creating, setCreating] = useState(false);

  async function load() {
    setLoading(true);
    const { data, error } = await supabase
      .from("courses")
      .select("*")
      .order("sort_order", { ascending: true })
      .order("updated_at", { ascending: false });
    if (error) setError(error.message);
    else setItems(data ?? []);
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((c) => {
      if (statusFilter !== "all" && c.status !== statusFilter) return false;
      if (!q) return true;
      return c.title.toLowerCase().includes(q) || c.slug.toLowerCase().includes(q);
    });
  }, [items, query, statusFilter]);

  async function onCreate() {
    if (creating) return;
    setCreating(true);
    setError(null);
    const baseTitle = "Neuer Kurs";
    let slug = slugify(`${baseTitle}-${Date.now().toString(36)}`);
    const { data, error } = await supabase
      .from("courses")
      .insert({ title: baseTitle, slug, description: "", status: "draft" })
      .select("id")
      .single();
    setCreating(false);
    if (error || !data) {
      setError(error?.message ?? "Kurs konnte nicht angelegt werden.");
      return;
    }
    navigate({ to: "/admin/courses/$id", params: { id: data.id } });
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Kurse</h1>
          <p className="text-sm text-muted-foreground">Kurse anlegen, strukturieren und veröffentlichen.</p>
        </div>
        <button
          onClick={onCreate}
          disabled={creating}
          className="inline-flex items-center gap-2 rounded-md bg-brand-button px-4 py-2 text-sm font-semibold text-brand-button-foreground disabled:opacity-60"
        >
          {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          Neuer Kurs
        </button>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Suche nach Titel oder Slug…"
            className="w-72 rounded-md border border-input bg-background py-2 pl-9 pr-3 text-sm"
          />
        </div>
        <div className="inline-flex overflow-hidden rounded-md border border-input text-sm">
          {(["all", "draft", "published"] as const).map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-2 ${statusFilter === s ? "bg-primary text-primary-foreground" : "bg-background text-foreground hover:bg-secondary"}`}
            >
              {s === "all" ? "Alle" : s === "draft" ? "Entwurf" : "Veröffentlicht"}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Lade Kurse…
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-md border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          Keine Kurse gefunden. Legen Sie über „Neuer Kurs" den ersten Kurs an.
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border border-border">
          <table className="min-w-full divide-y divide-border text-sm">
            {/*
              Ticket AV6: Statusspalte entfernt (3 von 3 veröffentlicht). Entwürfe
              werden am Titel gekennzeichnet, der Statusfilter bleibt bestehen.
            */}
            <thead className="bg-secondary/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Titel</th>
                <th className="px-4 py-3">Slug</th>
                <th className="px-4 py-3">Aktualisiert</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border bg-background">
              {filtered.map((c) => (
                <tr key={c.id} className="hover:bg-secondary/30">
                  <td className="px-4 py-3">
                    <Link
                      to="/admin/courses/$id"
                      params={{ id: c.id }}
                      className="font-medium text-foreground hover:text-primary"
                    >
                      {c.title || "Ohne Titel"}
                    </Link>
                    {c.status !== "published" && (
                      <span className="ml-2 rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-foreground">
                        Entwurf
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{c.slug}</td>

                  <td className="px-4 py-3 text-muted-foreground">
                    {dateFmt.format(new Date(c.updated_at))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
